import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import { BuiltLogoMark } from './ui/BuiltLogoMark';
import {
  ADMIN_NAV_GROUPS,
  adminPathInGroup,
  isAdminHrefActive,
  type AdminNavItem,
} from '../admin/adminNav';

function envBadge(): { label: string; tone: string } {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  if (host.includes('localhost') || host.includes('127.0.0.1')) {
    return { label: 'Local', tone: 'bg-sky-500/15 text-sky-300 border-sky-500/30' };
  }
  if (host.startsWith('staging.')) {
    return { label: 'Staging', tone: 'bg-amber-500/15 text-amber-200 border-amber-500/30' };
  }
  if (host.includes('biltspec')) {
    return { label: 'Production', tone: 'bg-rose-500/15 text-rose-200 border-rose-500/30' };
  }
  return { label: 'Admin', tone: 'bg-white/10 text-gray-300 border-white/20' };
}

const leafClass = (active: boolean, nested = false) =>
  `flex items-center rounded-lg text-sm font-medium transition-colors duration-200 min-h-[40px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 ${
    nested ? 'pl-3 pr-3 py-1.5 text-[13px]' : 'px-3 py-2'
  } ${
    active
      ? 'bg-orange-500/[0.1] text-orange-300 border border-orange-500/30'
      : 'text-gray-300 border border-transparent hover:bg-white/[0.04] hover:text-white'
  }`;

const parentClass = (sectionActive: boolean) =>
  `flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-200 min-h-[40px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 border border-transparent ${
    sectionActive
      ? 'text-orange-200/90 hover:text-orange-100'
      : 'text-gray-400 hover:bg-white/[0.04] hover:text-white'
  }`;

function NavItemBlock({
  item,
  pathname,
  search,
  onNavigate,
}: {
  item: AdminNavItem;
  pathname: string;
  search: string;
  onNavigate: () => void;
}) {
  const children = item.children || [];
  const hasChildren = children.length > 0;

  if (!hasChildren) {
    const active = isAdminHrefActive(item.href, pathname, search);
    return (
      <li>
        <Link
          to={item.href}
          onClick={onNavigate}
          className={leafClass(active)}
          aria-current={active ? 'page' : undefined}
        >
          <span className="truncate">{item.name}</span>
        </Link>
      </li>
    );
  }

  const childActive = children.some((child) => isAdminHrefActive(child.href, pathname, search));
  const parentExactActive = isAdminHrefActive(item.href, pathname, search);

  return (
    <li>
      <Link
        to={item.href}
        onClick={onNavigate}
        className={parentClass(childActive || parentExactActive)}
        aria-current={parentExactActive ? 'page' : undefined}
      >
        <span className="truncate">{item.name}</span>
      </Link>
      <ul className="mt-0.5 mb-1 space-y-0.5 border-l border-white/[0.06] ml-3" aria-label={`${item.name} sections`}>
        {children.map((child) => {
          const active = isAdminHrefActive(child.href, pathname, search);
          return (
            <li key={child.href}>
              <Link
                to={child.href}
                onClick={onNavigate}
                className={leafClass(active, true)}
                aria-current={active ? 'page' : undefined}
              >
                <span className="truncate">{child.name}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </li>
  );
}

export const AdminLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const badge = useMemo(() => envBadge(), []);

  // Keep the group that owns the current route expanded.
  useEffect(() => {
    setCollapsed((prev) => {
      const next = { ...prev };
      for (const group of ADMIN_NAV_GROUPS) {
        if (adminPathInGroup(group, location.pathname)) {
          next[group.id] = false;
        }
      }
      return next;
    });
  }, [location.pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const closeMobile = () => setIsMobileMenuOpen(false);

  const nav = (
    <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto" aria-label="Admin">
      {ADMIN_NAV_GROUPS.map((group) => {
        const isCollapsed = !!collapsed[group.id];
        return (
          <div key={group.id}>
            <button
              type="button"
              className="flex w-full items-center justify-between px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded"
              onClick={() => setCollapsed((c) => ({ ...c, [group.id]: !c[group.id] }))}
              aria-expanded={!isCollapsed}
            >
              <span>{group.label}</span>
              <span aria-hidden="true" className="text-gray-600">
                {isCollapsed ? '+' : '−'}
              </span>
            </button>
            {!isCollapsed && (
              <ul className="mt-1 space-y-0.5">
                {group.items.map((item) => (
                  <NavItemBlock
                    key={item.href}
                    item={item}
                    pathname={location.pathname}
                    search={location.search}
                    onNavigate={closeMobile}
                  />
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen overflow-x-hidden flex flex-col bg-[#0a0a0a]">
      <a
        href="#admin-main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-3 focus:left-3 focus:z-[60] focus:rounded-lg focus:bg-orange-500 focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-black"
      >
        Skip to admin content
      </a>

      <div className="lg:hidden">
        <div className="flex items-center justify-between p-4 border-b border-white/[0.06] bg-[#0c0c0c]">
          <div className="flex items-center min-w-0 flex-1 gap-3">
            <BuiltLogoMark size="sm" />
            <div className="min-w-0">
              <h1 className="font-display text-lg font-semibold tracking-tight text-white truncate">Admin</h1>
              <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium ${badge.tone}`}>
                {badge.label}
              </span>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-gray-400 hover:text-white p-2 flex-shrink-0 min-h-[44px] min-w-[44px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50 rounded-lg"
            aria-expanded={isMobileMenuOpen}
            aria-controls="admin-mobile-nav"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>

      <div className="flex flex-1">
        <aside
          id="admin-mobile-nav"
          className={`${isMobileMenuOpen ? 'block' : 'hidden'} lg:block fixed lg:static inset-y-0 left-0 z-50 w-64 border-r border-white/[0.06] lg:min-h-screen bg-[#0c0c0c]`}
        >
          <div className="flex flex-col h-full">
            <div className="hidden lg:flex items-center justify-between px-4 py-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3 min-w-0">
                <BuiltLogoMark size="sm" />
                <div className="min-w-0">
                  <h1 className="font-display text-lg font-semibold tracking-tight text-white">Admin</h1>
                  <span className={`inline-flex mt-0.5 px-1.5 py-0.5 rounded border text-[10px] font-medium ${badge.tone}`}>
                    {badge.label}
                  </span>
                </div>
              </div>
            </div>

            {nav}

            <div className="border-t border-white/[0.06] p-4">
              <div className="flex items-center mb-3">
                <div className="w-10 h-10 bg-gradient-brand-tile rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-medium">
                    {user?.username?.charAt(0).toUpperCase() || 'A'}
                  </span>
                </div>
                <div className="ml-3 min-w-0 flex-1">
                  <p className="text-sm font-medium text-white truncate">{user?.username}</p>
                  <p className="text-xs text-gray-500">Super Admin</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Link
                  to="/"
                  onClick={closeMobile}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors duration-200 min-h-[40px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                >
                  Back to Bilt
                </Link>
                <button
                  type="button"
                  onClick={() => void handleLogout()}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-300 hover:bg-white/[0.04] hover:text-white rounded-lg transition-colors duration-200 min-h-[40px] cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500/50"
                >
                  Logout
                </button>
              </div>
            </div>
          </div>
        </aside>

        {isMobileMenuOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-black/50 z-40 lg:hidden cursor-pointer"
            aria-label="Close menu overlay"
            onClick={closeMobile}
          />
        )}

        <div className="flex-1 lg:ml-0 min-w-0 overflow-x-hidden">
          <main id="admin-main" className="p-4 sm:p-6 max-w-[1400px]" tabIndex={-1}>
            <Outlet />
          </main>
        </div>
      </div>

      <Footer />
    </div>
  );
};
